import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { PieChart, Pie, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from 'recharts'
import { collection, query, where, orderBy, onSnapshot } from 'firebase/firestore'
import { db } from '../../firebase.config'
import { useAuth } from '../../context/AuthContext'
import AppLayout from '../../components/layout/AppLayout'
import LessonModal from '../../components/shared/LessonModal'

const CATEGORY_COLORS = ['#9333ea', '#2563eb', '#16a34a', '#dc2626']
const STATUS_COLORS = { Open: '#dc2626', Reviewed: '#2563eb', Closed: '#16a34a' }
const SEVERITY_COLORS = { Low: '#3b82f6', Medium: '#f59e0b', High: '#f97316', Critical: '#dc2626' }
const WORK_AREAS = ['Substation', 'BESS', 'Collection', 'TLine']
const CATEGORIES = ['Engineering', 'Execution', 'Project Quality', 'Safety']
const ROLE_ORDER = ['member', 'foreman', 'superintendant', 'supervisor', 'admin']
const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']

function getWeekDates(weekOffset = 0): Date[] {
  const now = new Date()
  const day = now.getDay()
  const diff = now.getDate() - day + (day === 0 ? -6 : 1)
  const monday = new Date(now.setDate(diff + weekOffset * 7))
  monday.setHours(0, 0, 0, 0)
  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date(monday)
    d.setDate(monday.getDate() + i)
    d.setHours(0, 0, 0, 0)
    return d
  })
}

function formatDbDate(d: Date) {
  return d.toISOString().split('T')[0]
}

function formatShort(d: Date) {
  return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })
}

function formatDate(iso: string) {
  if (!iso) return ''
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

function calcTotal(entries: any[], field: string): number {
  return entries.reduce((sum: number, e: any) => sum + (parseFloat(e[field]) || 0), 0)
}

export default function DashboardPage() {
  const { orgId, userData, isSupervisor, isAdmin } = useAuth()
  const navigate = useNavigate()
  const role = userData?.role || 'member'
  const isMember = role === 'member'
  const isForeman = role === 'foreman'
  const isSuperintendant = role === 'superintendant'
  const isSupervisorOrAbove = ['foreman', 'superintendant', 'supervisor', 'admin'].includes(role)

  const [lessons, setLessons] = useState<any[]>([])
  const [activity, setActivity] = useState<any[]>([])
  const [timesheets, setTimesheets] = useState<any[]>([])
  const [messages, setMessages] = useState<any[]>([])
  const [reports, setReports] = useState<any[]>([])
  const [users, setUsers] = useState<any[]>([])
  const [toolboxTalks, setToolboxTalks] = useState<any[]>([])
  const [nearMiss, setNearMiss] = useState<any[]>([])
  const [pendingAttendance, setPendingAttendance] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [showModal, setShowModal] = useState(false)
  const [filterStatus, setFilterStatus] = useState('')
  const [filterSeverity, setFilterSeverity] = useState('')
  const [filterWorkArea, setFilterWorkArea] = useState('')
  const [weekOffset, setWeekOffset] = useState(0)

  const weekDates = getWeekDates(weekOffset)
  const weekStart = weekDates[0]
  const weekEnd = weekDates[6]

  useEffect(() => {
    if (!orgId) return

    const unsubLessons = onSnapshot(
      query(collection(db, 'organizations', orgId, 'lessons'), where('orgId', '==', orgId), orderBy('createdAt', 'desc')),
      snap => { setLessons(snap.docs.map(d => ({ id: d.id, ...d.data() }))); setLoading(false) }
    )

    const unsubActivity = onSnapshot(
      query(collection(db, 'organizations', orgId, 'activityFeed'), where('orgId', '==', orgId), orderBy('createdAt', 'desc')),
      snap => setActivity(snap.docs.slice(0, 20).map(d => ({ id: d.id, ...d.data() })))
    )

    const unsubTimesheets = onSnapshot(
      query(collection(db, 'organizations', orgId, 'timesheets'), where('orgId', '==', orgId), orderBy('createdAt', 'desc')),
      snap => setTimesheets(snap.docs.map(d => ({ id: d.id, ...d.data() })))
    )

    const unsubMessages = onSnapshot(
      query(collection(db, 'organizations', orgId, 'communications'), orderBy('createdAt', 'desc')),
      snap => setMessages(snap.docs.map(d => ({ id: d.id, ...d.data() })))
    )

    const unsubReports = onSnapshot(
      query(collection(db, 'organizations', orgId, 'dailyReports'), orderBy('createdAt', 'desc')),
      snap => setReports(snap.docs.map(d => ({ id: d.id, ...d.data() })))
    )

    const unsubUsers = onSnapshot(
      query(collection(db, 'organizations', orgId, 'users')),
      snap => setUsers(snap.docs.map(d => ({ id: d.id, ...d.data() })))
    )

    const unsubToolbox = onSnapshot(
      query(collection(db, 'organizations', orgId, 'toolboxTalks'), orderBy('createdAt', 'desc')),
      snap => setToolboxTalks(snap.docs.map(d => ({ id: d.id, ...d.data() })))
    )

    const unsubNearMiss = onSnapshot(
      query(collection(db, 'organizations', orgId, 'nearMiss'), orderBy('createdAt', 'desc')),
      snap => setNearMiss(snap.docs.map(d => ({ id: d.id, ...d.data() })))
    )

    if (!userData?.uid) return
    const unsubAttendance = onSnapshot(
      query(
        collection(db, 'organizations', orgId, 'attendanceRequests'),
        where('supervisorId', '==', userData.uid),
        where('status', '==', 'pending'),
        orderBy('createdAt', 'desc')
      ),
      snap => setPendingAttendance(snap.docs.map(d => ({ id: d.id, ...d.data() })))
    )

    return () => { unsubLessons(); unsubActivity(); unsubTimesheets(); unsubMessages(); unsubReports(); unsubUsers(); unsubToolbox(); unsubNearMiss(); unsubAttendance() }
  }, [orgId])

  // My timesheet for current week
  const weekStr = formatDbDate(weekStart)
  const myTimesheet = timesheets.find(t => t.userId === userData?.uid && t.weekOf === weekStr)
  const myEntries = myTimesheet?.entries || DAYS.map(() => ({ straightHours: '', overtimeHours: '', jobNumber: '', notes: '' }))
  const totalST = calcTotal(myEntries, 'straightHours')
  const totalOT = calcTotal(myEntries, 'overtimeHours')

  // My tasking messages
  const myTasks = messages.filter(m => {
    if (!m.isTask) return false
    const myStatus = m.taskStatus?.find((t: any) => t.userId === userData?.uid || t.userId === 'all')
    return myStatus && (myStatus.status === 'acknowledged' || myStatus.status === 'completed')
  })

  // My daily reports
  const myReports = reports.filter(r => r.submittedByUid === userData?.uid)
  const myEscalated = myReports.filter(r => r.escalate).length

  // Timesheets pending my review (supervisor+)
  const pendingApproval = timesheets.filter(ts => {
    if (!ts || ts.status !== 'submitted') return false
    const reviewerRole = ts.submittedToRole || 'superintendant'
    return ROLE_ORDER.indexOf(role) >= ROLE_ORDER.indexOf(reviewerRole)
  })

  // Daily report KPIs
  const totalReports = reports.length
  const escalatedReports = reports.filter(r => r.escalate).length
  const last7daysReports = reports.filter(r => new Date(r.createdAt) > new Date(Date.now() - 7 * 86400000)).length

  // Pending users
  const pendingUsers = users.filter(u => u.status === 'pending')

  // My toolbox talks
  const myToolbox = toolboxTalks.filter(t => t.createdByUid === userData?.uid)

  // Near Miss / Good Catch — weekly compliance
  // Count entries created by me this rolling 7-day window
  const sevenDaysAgo = new Date(Date.now() - 7 * 86400000)
  const myNearMissThisWeek = nearMiss.filter(n => n.createdByUid === userData?.uid && new Date(n.createdAt) >= sevenDaysAgo)
  const nearMissCount = myNearMissThisWeek.length
  const nearMissMet = nearMissCount > 0

  // Timesheet weekly compliance — member needs to submit a timesheet for the current week
  const timesheetMet = myTimesheet && (myTimesheet.status === 'submitted' || myTimesheet.status === 'approved')

  // Countdown: days left until Sunday midnight
  const now = new Date()
  const dayOfWeek = now.getDay()
  const daysUntilSunday = dayOfWeek === 0 ? 0 : 7 - dayOfWeek
  const sundayMidnight = new Date(now)
  sundayMidnight.setDate(now.getDate() + daysUntilSunday)
  sundayMidnight.setHours(23, 59, 59, 999)
  const msLeft = sundayMidnight.getTime() - now.getTime()
  const hoursLeft = Math.floor(msLeft / (1000 * 60 * 60))
  const minsLeft = Math.floor((msLeft % (1000 * 60 * 60)) / (1000 * 60))

  const recent = lessons.slice(0, 10)

  if (loading) {
    return <AppLayout title="Dashboard"><div className="flex items-center justify-center h-64"><div className="animate-spin w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full" /></div></AppLayout>
  }

  // ═══════════════════════════════════════════════════════════
  // MEMBER DASHBOARD
  // ═══════════════════════════════════════════════════════════
  if (isMember) {
    return (
      <AppLayout title="Dashboard">
        <div className="mb-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => setWeekOffset(w => w - 1)} className="px-3 py-1.5 rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-50 text-sm">← Prev</button>
            <span className="font-semibold text-gray-800 text-sm">
              {weekStart.toLocaleDateString('en-US', { month: 'long', day: 'numeric' })} – {weekEnd.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
            </span>
            <button onClick={() => setWeekOffset(w => w + 1)} className="px-3 py-1.5 rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-50 text-sm">Next →</button>
            {weekOffset !== 0 && <button onClick={() => setWeekOffset(0)} className="text-xs text-blue-600 hover:underline">This Week</button>}
          </div>
          <span className="text-xs text-gray-500">Week {weekOffset === 0 ? 'of ' : ''}{weekStart.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
          {/* Timesheet Compliance */}
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-gray-900 text-sm">⏱ Timesheet</h3>
              <span className={`px-2 py-0.5 rounded text-xs font-medium ${timesheetMet ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
                {timesheetMet ? '✓ Submitted' : '⏱ Due this week'}
              </span>
            </div>
            {!timesheetMet ? (
              <div className="text-center">
                <p className="text-3xl font-bold text-red-600 mb-1">{hoursLeft}h {minsLeft}m</p>
                <p className="text-xs text-gray-500 mb-3">remaining to submit</p>
                <button
                  onClick={() => navigate('/timesheets')}
                  className="w-full bg-red-600 text-white py-2 rounded-lg text-sm font-medium hover:bg-red-700"
                >
                  + Log Timesheet
                </button>
              </div>
            ) : (
              <div className="text-center">
                <p className="text-2xl font-bold text-green-600 mb-1">All Caught Up ✓</p>
                <p className="text-xs text-gray-500 mb-3">Timesheet submitted</p>
                <button
                  onClick={() => navigate('/timesheets')}
                  className="w-full bg-green-50 text-green-700 border border-green-200 py-2 rounded-lg text-sm font-medium hover:bg-green-100"
                >
                  View / Edit
                </button>
              </div>
            )}
          </div>

          {/* My Tasks */}
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-gray-900 text-sm">My Tasks</h3>
              <span className="text-xs bg-purple-50 text-purple-700 px-2 py-0.5 rounded font-medium">{myTasks.length}</span>
            </div>
            {myTasks.length === 0 ? (
              <p className="text-xs text-gray-400">No assigned tasks</p>
            ) : (
              <div className="space-y-3">
                {myTasks.slice(0, 5).map(m => {
                  const myStatus = m.taskStatus?.find((t: any) => t.userId === userData?.uid || t.userId === 'all')
                  return (
                    <div key={m.id} className="border-b border-gray-100 pb-2">
                      <p className="text-sm font-medium text-gray-800 truncate">{m.title}</p>
                      <p className="text-xs capitalize" style={{ color: myStatus?.status === 'completed' ? '#16a34a' : '#2563eb' }}>
                        {myStatus?.status || 'pending'}
                      </p>
                    </div>
                  )
                })}
              </div>
            )}
            <button onClick={() => navigate('/communications')} className="mt-3 w-full text-xs text-blue-600 hover:underline text-center">View Communications →</button>
          </div>

          {/* Near Miss Compliance */}
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-gray-900 text-sm">🚨 Near Miss / Good Catch</h3>
              <span className={`px-2 py-0.5 rounded text-xs font-medium ${nearMissMet ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
                {nearMissMet ? `✓ ${nearMissCount} this week` : '⏱ Due this week'}
              </span>
            </div>
            {!nearMissMet ? (
              <div className="text-center">
                <p className="text-3xl font-bold text-red-600 mb-1">{hoursLeft}h {minsLeft}m</p>
                <p className="text-xs text-gray-500 mb-3">remaining to submit</p>
                <button
                  onClick={() => navigate('/near-miss')}
                  className="w-full bg-red-600 text-white py-2 rounded-lg text-sm font-medium hover:bg-red-700"
                >
                  + Log Near Miss / Good Catch
                </button>
              </div>
            ) : (
              <div className="text-center">
                <p className="text-2xl font-bold text-green-600 mb-1">All Caught Up ✓</p>
                <p className="text-xs text-gray-500 mb-3">{nearMissCount} submitted this week</p>
                <button
                  onClick={() => navigate('/near-miss')}
                  className="w-full bg-green-50 text-green-700 border border-green-200 py-2 rounded-lg text-sm font-medium hover:bg-green-100"
                >
                  + Add Another
                </button>
              </div>
            )}
          </div>
        </div>

        {/* My Daily Reports */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm mb-6">
          <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between">
            <h3 className="font-semibold text-gray-700 text-sm">My Daily Reports</h3>
            <span className="text-xs text-gray-500">{myReports.length} total{myEscalated > 0 ? ` · ${myEscalated} escalated` : ''}</span>
          </div>
          {myReports.length === 0 ? (
            <p className="px-5 py-6 text-center text-gray-400 text-sm">No daily reports submitted</p>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="text-left px-5 py-3 font-medium text-gray-600">Date</th>
                  <th className="text-left px-5 py-3 font-medium text-gray-600">Project</th>
                  <th className="text-left px-5 py-3 font-medium text-gray-600">Escalated</th>
                  <th className="text-left px-5 py-3 font-medium text-gray-600">Status</th>
                </tr>
              </thead>
              <tbody>
                {myReports.slice(0, 5).map(r => (
                  <tr key={r.id} className="border-b border-gray-100">
                    <td className="px-5 py-3 font-medium text-gray-900">{r.date || '-'}</td>
                    <td className="px-5 py-3 text-gray-600">{r.projectName || '-'}</td>
                    <td className="px-5 py-3">{r.escalate ? <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded font-medium">🔺 Yes</span> : <span className="text-xs text-gray-400">No</span>}</td>
                    <td className="px-5 py-3"><span className={`px-2 py-0.5 rounded text-xs font-medium ${r.reportStatus === 'reviewed' ? 'bg-blue-100 text-blue-700' : 'bg-gray-50 text-gray-400'}`}>{r.reportStatus || 'Pending'}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <button onClick={() => navigate('/reports')} className="px-5 py-3 text-xs text-blue-600 hover:underline">View all reports →</button>
        </div>

        {/* Activity Feed */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
          <div className="px-5 py-4 border-b border-gray-200">
            <h3 className="font-semibold text-gray-700 text-sm">Recent Activity</h3>
          </div>
          <div className="divide-y divide-gray-100 max-h-64 overflow-y-auto">
            {activity.slice(0, 15).map(a => (
              <div key={a.id} className="px-5 py-3 flex items-start gap-3">
                <div className="w-2 h-2 mt-1.5 rounded-full bg-blue-500 flex-shrink-0" />
                <div>
                  <p className="text-sm text-gray-700">{a.message || a.type}</p>
                  <p className="text-xs text-gray-400 mt-0.5">{formatDate(a.createdAt)}</p>
                </div>
              </div>
            ))}
            {activity.length === 0 && <p className="px-5 py-6 text-center text-gray-400 text-sm">No recent activity</p>}
          </div>
        </div>
      </AppLayout>
    )
  }

  // ═══════════════════════════════════════════════════════════
  // FOREMAN DASHBOARD
  // ═══════════════════════════════════════════════════════════
  if (isForeman) {
    return (
      <AppLayout title="Dashboard">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">Foreman Dashboard</h2>
          <button onClick={() => navigate('/communications')} className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-blue-700">
            + Compose Tasking
          </button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
          {/* Timesheet Compliance */}
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-gray-900 text-sm">⏱ Timesheet</h3>
              <span className={`px-2 py-0.5 rounded text-xs font-medium ${timesheetMet ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
                {timesheetMet ? '✓ Submitted' : '⏱ Due this week'}
              </span>
            </div>
            {!timesheetMet ? (
              <div className="text-center">
                <p className="text-3xl font-bold text-red-600 mb-1">{hoursLeft}h {minsLeft}m</p>
                <p className="text-xs text-gray-500 mb-3">remaining to submit</p>
                <button onClick={() => navigate('/timesheets')} className="w-full bg-red-600 text-white py-2 rounded-lg text-sm font-medium hover:bg-red-700">
                  + Log Timesheet
                </button>
              </div>
            ) : (
              <div className="text-center">
                <p className="text-2xl font-bold text-green-600 mb-1">All Caught Up ✓</p>
                <p className="text-xs text-gray-500 mb-3">Timesheet submitted</p>
                <button onClick={() => navigate('/timesheets')} className="w-full bg-green-50 text-green-700 border border-green-200 py-2 rounded-lg text-sm font-medium hover:bg-green-100">View / Edit</button>
              </div>
            )}
          </div>

          {/* Near Miss Compliance */}
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-gray-900 text-sm">🚨 Near Miss / Good Catch</h3>
              <span className={`px-2 py-0.5 rounded text-xs font-medium ${nearMissMet ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
                {nearMissMet ? `✓ ${nearMissCount} this week` : '⏱ Due this week'}
              </span>
            </div>
            {!nearMissMet ? (
              <div className="text-center">
                <p className="text-3xl font-bold text-red-600 mb-1">{hoursLeft}h {minsLeft}m</p>
                <p className="text-xs text-gray-500 mb-3">remaining to submit</p>
                <button onClick={() => navigate('/near-miss')} className="w-full bg-red-600 text-white py-2 rounded-lg text-sm font-medium hover:bg-red-700">
                  + Log Near Miss / Good Catch
                </button>
              </div>
            ) : (
              <div className="text-center">
                <p className="text-2xl font-bold text-green-600 mb-1">All Caught Up ✓</p>
                <p className="text-xs text-gray-500 mb-3">{nearMissCount} submitted this week</p>
                <button onClick={() => navigate('/near-miss')} className="w-full bg-green-50 text-green-700 border border-green-200 py-2 rounded-lg text-sm font-medium hover:bg-green-100">+ Add Another</button>
              </div>
            )}
          </div>

          {/* Crew Status */}
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
            <h3 className="font-semibold text-gray-900 text-sm mb-3">Crew Status</h3>
            <div className="space-y-3">
              {timesheets.filter(t => t.userId !== userData?.uid).slice(0, 6).map(ts => (
                <div key={ts.id} className="flex items-center justify-between text-sm">
                  <div>
                    <p className="font-medium text-gray-800">{ts.userName || 'Unknown'}</p>
                    <p className="text-xs text-gray-400">Week of {ts.weekOf || '—'}</p>
                  </div>
                  <span className={`px-2 py-0.5 rounded text-xs font-medium ${ts.status === 'approved' ? 'bg-green-50 text-green-700' : ts.status === 'submitted' ? 'bg-amber-50 text-amber-700' : 'bg-gray-50 text-gray-400'}`}>
                    {ts.status || 'draft'}
                  </span>
                </div>
              ))}
              {timesheets.filter(t => t.userId !== userData?.uid).length === 0 && (
                <p className="text-xs text-gray-400">No crew timesheets yet</p>
              )}
            </div>
          </div>

          {/* Timesheets Pending My Review */}
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-gray-900 text-sm">Pending My Review</h3>
              <span className="text-xs bg-amber-50 text-amber-700 px-2 py-0.5 rounded font-medium">{pendingApproval.length}</span>
            </div>
            {pendingApproval.length === 0 ? (
              <p className="text-xs text-gray-400">No timesheets awaiting your approval</p>
            ) : (
              <div className="space-y-2">
                {pendingApproval.slice(0, 4).map(ts => (
                  <div key={ts.id} className="flex items-center justify-between text-sm">
                    <div>
                      <p className="font-medium text-gray-800">{ts.userName || 'Unknown'}</p>
                      <p className="text-xs text-gray-400">Week of {ts.weekOf || '—'}</p>
                    </div>
                    <button onClick={() => navigate('/timesheets')} className="text-xs text-blue-600 hover:underline">Review</button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Tasking + My Reports row */}
        <div className="grid grid-cols-2 gap-6 mb-6">
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-gray-900 text-sm">My Tasks ({myTasks.length})</h3>
            </div>
            {myTasks.length === 0 ? <p className="text-xs text-gray-400">No tasks assigned</p> : (
              <div className="space-y-2">{myTasks.slice(0, 4).map(m => {
                const myStatus = m.taskStatus?.find((t: any) => t.userId === userData?.uid || t.userId === 'all')
                return (
                  <div key={m.id} className="flex items-center justify-between text-sm">
                    <p className="font-medium text-gray-800 truncate max-w-[200px]">{m.title}</p>
                    <span className="text-xs capitalize" style={{ color: myStatus?.status === 'completed' ? '#16a34a' : '#2563eb' }}>{myStatus?.status}</span>
                  </div>
                )
              })}</div>
            )}
          </div>

          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-gray-900 text-sm">My Daily Reports ({myReports.length})</h3>
              {myEscalated > 0 && <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded font-medium">{myEscalated} escalated</span>}
            </div>
            {myReports.length === 0 ? <p className="text-xs text-gray-400">No reports submitted</p> : (
              <div className="space-y-2">{myReports.slice(0, 4).map(r => (
                <div key={r.id} className="flex items-center justify-between text-sm">
                  <div>
                    <p className="font-medium text-gray-800">{r.date}</p>
                    <p className="text-xs text-gray-400">{r.projectName}</p>
                  </div>
                  {r.escalate && <span className="text-xs text-red-600">🔺</span>}
                </div>
              ))}</div>
            )}
          </div>
        </div>

        {/* Activity Feed */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
          <div className="px-5 py-4 border-b border-gray-200"><h3 className="font-semibold text-gray-700 text-sm">Recent Activity</h3></div>
          <div className="divide-y divide-gray-100 max-h-64 overflow-y-auto">
            {activity.slice(0, 15).map(a => (
              <div key={a.id} className="px-5 py-3 flex items-start gap-3">
                <div className="w-2 h-2 mt-1.5 rounded-full bg-blue-500 flex-shrink-0" />
                <div><p className="text-sm text-gray-700">{a.message || a.type}</p><p className="text-xs text-gray-400 mt-0.5">{formatDate(a.createdAt)}</p></div>
              </div>
            ))}
            {activity.length === 0 && <p className="px-5 py-6 text-center text-gray-400 text-sm">No recent activity</p>}
          </div>
        </div>
      </AppLayout>
    )
  }

  // ═══════════════════════════════════════════════════════════
  // SUPERINTENDANT / SUPERVISOR / ADMIN DASHBOARD
  // ═══════════════════════════════════════════════════════════
  console.log('ADMIN UID:', userData?.uid, 'pendingAttendance:', pendingAttendance.length, 'isSupOrAbove:',
  isSupervisorOrAbove)
  return (
    <AppLayout title="Dashboard">
      {/* KPIs */}
      <div className="grid grid-cols-4 gap-6 mb-8">
        {[
          { label: 'Total Lessons', value: lessons.length, color: 'text-gray-900' },
          { label: 'Escalated Reports', value: escalatedReports, color: 'text-red-600' },
          { label: 'Timesheets Pending', value: pendingApproval.length, color: 'text-amber-600' },
          { label: 'User Approvals', value: pendingUsers.length, color: 'text-yellow-600' },
        ].map(kpi => (
          <div key={kpi.label} className="bg-white rounded-xl p-5 border border-gray-200 shadow-sm">
            <p className="text-3xl font-bold mb-1" style={{ color: kpi.color.includes('.') ? undefined : kpi.color }}>{kpi.value}</p>
            <p className="text-sm text-gray-500">{kpi.label}</p>
          </div>
        ))}
      </div>

      {/* My compliance strip */}
      <div className="grid grid-cols-2 gap-6 mb-8">
        {/* My Timesheet Compliance */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold text-gray-900 text-sm">⏱ My Timesheet</h3>
            <span className={`px-2 py-0.5 rounded text-xs font-medium ${timesheetMet ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
              {timesheetMet ? '✓ Submitted' : '⏱ Due this week'}
            </span>
          </div>
          {!timesheetMet ? (
            <div className="text-center">
              <p className="text-3xl font-bold text-red-600 mb-1">{hoursLeft}h {minsLeft}m</p>
              <p className="text-xs text-gray-500 mb-3">remaining to submit</p>
              <button onClick={() => navigate('/timesheets')} className="w-full bg-red-600 text-white py-2 rounded-lg text-sm font-medium hover:bg-red-700">+ Log Timesheet</button>
            </div>
          ) : (
            <div className="text-center">
              <p className="text-2xl font-bold text-green-600 mb-1">All Caught Up ✓</p>
              <p className="text-xs text-gray-500 mb-3">Timesheet submitted</p>
              <button onClick={() => navigate('/timesheets')} className="w-full bg-green-50 text-green-700 border border-green-200 py-2 rounded-lg text-sm font-medium hover:bg-green-100">View / Edit</button>
            </div>
          )}
        </div>

        {/* My Near Miss Compliance */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold text-gray-900 text-sm">🚨 Near Miss / Good Catch</h3>
            <span className={`px-2 py-0.5 rounded text-xs font-medium ${nearMissMet ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
              {nearMissMet ? `✓ ${nearMissCount} this week` : '⏱ Due this week'}
            </span>
          </div>
          {!nearMissMet ? (
            <div className="text-center">
              <p className="text-3xl font-bold text-red-600 mb-1">{hoursLeft}h {minsLeft}m</p>
              <p className="text-xs text-gray-500 mb-3">remaining to submit</p>
              <button onClick={() => navigate('/near-miss')} className="w-full bg-red-600 text-white py-2 rounded-lg text-sm font-medium hover:bg-red-700">+ Log Near Miss / Good Catch</button>
            </div>
          ) : (
            <div className="text-center">
              <p className="text-2xl font-bold text-green-600 mb-1">All Caught Up ✓</p>
              <p className="text-xs text-gray-500 mb-3">{nearMissCount} submitted this week</p>
              <button onClick={() => navigate('/near-miss')} className="w-full bg-green-50 text-green-700 border border-green-200 py-2 rounded-lg text-sm font-medium hover:bg-green-100">+ Add Another</button>
            </div>
          )}
        </div>
      </div>

      {/* Supervisor action strip */}
      {isSupervisorOrAbove && (
        <div className="grid grid-cols-4 gap-6 mb-8">
          {/* Timesheets Pending */}
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-gray-700">Timesheets Pending Approval</h3>
              <span className="text-xs bg-amber-50 text-amber-700 px-2 py-0.5 rounded font-medium">{pendingApproval.length}</span>
            </div>
            {pendingApproval.length === 0 ? (
              <p className="text-xs text-gray-400">No timesheets awaiting your approval</p>
            ) : (
              <div className="space-y-2">
                {pendingApproval.slice(0, 4).map(ts => (
                  <div key={ts.id} className="flex items-center justify-between text-sm">
                    <div>
                      <p className="font-medium text-gray-800">{ts.userName || 'Unknown'}</p>
                      <p className="text-xs text-gray-400">Week of {ts.weekOf || '—'}</p>
                    </div>
                    <button onClick={() => navigate('/timesheets')} className="text-xs text-blue-600 hover:underline">Review</button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Attendance Acknowledgments */}
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-gray-700">Attend. Acknowledgments</h3>
              <span className="text-xs bg-amber-50 text-amber-700 px-2 py-0.5 rounded font-medium">{pendingAttendance.length}</span>
            </div>
            {pendingAttendance.length === 0 ? (
              <p className="text-xs text-gray-400">No attendance requests pending</p>
            ) : (
              <div className="space-y-2">
                {pendingAttendance.slice(0, 4).map(r => (
                  <div key={r.id} className="flex items-center justify-between text-sm">
                    <div>
                      <p className="font-medium text-gray-800">{r.userName || 'Unknown'}</p>
                      <p className="text-xs text-gray-400">{r.type === 'late' ? 'Running Late' : 'Absent'}{r.comment ? ` — ${r.comment}` : ''}</p>
                    </div>
                    <button onClick={() => navigate('/attendance')} className="text-xs text-blue-600 hover:underline">Acknowledge</button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* My Tasks */}
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-gray-700">My Tasks</h3>
              <span className="text-xs bg-purple-50 text-purple-700 px-2 py-0.5 rounded font-medium">{myTasks.length}</span>
            </div>
            {myTasks.length === 0 ? <p className="text-xs text-gray-400">No acknowledged tasks</p> : (
              <div className="space-y-2">
                {myTasks.slice(0, 4).map(m => {
                  const myStatus = m.taskStatus?.find((t: any) => t.userId === userData?.uid || t.userId === 'all')
                  return (
                    <div key={m.id} className="flex items-center justify-between text-sm">
                      <div>
                        <p className="font-medium text-gray-800 truncate max-w-[180px]">{m.title}</p>
                        <p className="text-xs text-gray-400 capitalize">{myStatus?.status}</p>
                      </div>
                      <button onClick={() => navigate('/communications')} className="text-xs text-blue-600 hover:underline">View</button>
                    </div>
                  )
                })}
              </div>
            )}
          </div>

          {/* User Approvals */}
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-gray-700">User Approvals</h3>
              <span className="text-xs bg-yellow-50 text-yellow-700 px-2 py-0.5 rounded font-medium">{pendingUsers.length}</span>
            </div>
            {pendingUsers.length === 0 ? <p className="text-xs text-gray-400">No users awaiting approval</p> : (
              <div className="space-y-2">
                {pendingUsers.slice(0, 4).map(u => (
                  <div key={u.id} className="flex items-center justify-between text-sm">
                    <div>
                      <p className="font-medium text-gray-800">{u.name || 'Unknown'}</p>
                      <p className="text-xs text-gray-400">{u.email}</p>
                    </div>
                    <button onClick={() => navigate('/users')} className="text-xs text-blue-600 hover:underline">Review</button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Charts */}
      <div className="grid grid-cols-2 gap-6 mb-8">
        <div className="bg-white rounded-xl p-5 border border-gray-200 shadow-sm">
          <h3 className="text-sm font-semibold text-gray-700 mb-4">By Category</h3>
          <ResponsiveContainer width="100%" height={200}>
            <PieChart>
              <Pie data={CATEGORIES.map(c => ({ name: c, value: lessons.filter(l => l.category === c).length })).filter(d => d.value > 0)} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={50} outerRadius={80} paddingAngle={2}>
                {CATEGORIES.map((_, i) => <Cell key={i} fill={CATEGORY_COLORS[i % CATEGORY_COLORS.length]} />)}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
          <div className="flex flex-wrap gap-3 mt-3">
            {CATEGORIES.map((c, i) => (
              <div key={c} className="flex items-center gap-1.5 text-xs">
                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: CATEGORY_COLORS[i] }} />
                <span className="text-gray-600">{c}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="bg-white rounded-xl p-5 border border-gray-200 shadow-sm">
          <h3 className="text-sm font-semibold text-gray-700 mb-4">By Status</h3>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={['Open', 'Reviewed', 'Closed'].map(s => ({ name: s, count: lessons.filter(l => l.status === s).length }))}>
              <XAxis dataKey="name" tick={{ fontSize: 12 }} />
              <YAxis tick={{ fontSize: 12 }} />
              <Tooltip />
              <Bar dataKey="count" fill="#2563eb" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* New Lesson */}
      <div className="mb-6">
        <button onClick={() => setShowModal(true)} className="bg-accent text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-amber-700">+ New Lesson</button>
      </div>

      {/* Lessons Table */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm mb-6">
        <div className="px-5 py-4 border-b border-gray-200"><h3 className="text-sm font-semibold text-gray-700">Active Lessons</h3></div>
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="text-left px-5 py-3 font-medium text-gray-600">Title</th>
              <th className="text-left px-5 py-3 font-medium text-gray-600">Category</th>
              <th className="text-left px-5 py-3 font-medium text-gray-600">Severity</th>
              <th className="text-left px-5 py-3 font-medium text-gray-600">Status</th>
              <th className="text-left px-5 py-3 font-medium text-gray-600">Date</th>
            </tr>
          </thead>
          <tbody>
            {recent.map(lesson => (
              <tr key={lesson.id} className="border-b border-gray-100 hover:bg-gray-50 cursor-pointer" onClick={() => window.open(`/lessons/${lesson.id}`, '_blank')}>
                <td className="px-5 py-3 font-medium text-gray-900">{lesson.title}</td>
                <td className="px-5 py-3 text-gray-600">{lesson.category || '-'}</td>
                <td className="px-5 py-3">
                  <span className="px-2 py-1 rounded text-xs font-medium text-white" style={{ backgroundColor: SEVERITY_COLORS[lesson.severity as keyof typeof SEVERITY_COLORS] || '#888' }}>
                    {lesson.severity || 'Low'}
                  </span>
                </td>
                <td className="px-5 py-3">
                  <span className="px-2 py-1 rounded text-xs font-medium" style={{ backgroundColor: (STATUS_COLORS[lesson.status as keyof typeof STATUS_COLORS] || '#888') + '20', color: STATUS_COLORS[lesson.status as keyof typeof STATUS_COLORS] || '#888' }}>
                    {lesson.status || 'Open'}
                  </span>
                </td>
                <td className="px-5 py-3 text-gray-500">{formatDate(lesson.createdAt)}</td>
              </tr>
            ))}
            {recent.length === 0 && <tr><td colSpan={5} className="px-5 py-8 text-center text-gray-400">No lessons found</td></tr>}
          </tbody>
        </table>
      </div>

      {/* Activity Feed */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
        <div className="px-5 py-4 border-b border-gray-200"><h3 className="text-sm font-semibold text-gray-700">Recent Activity</h3></div>
        <div className="divide-y divide-gray-100 max-h-80 overflow-y-auto">
          {activity.map(a => (
            <div key={a.id} className="px-5 py-3 flex items-start gap-3">
              <div className="w-2 h-2 mt-1.5 rounded-full bg-blue-500 flex-shrink-0" />
              <div><p className="text-sm text-gray-700">{a.message || a.type}</p><p className="text-xs text-gray-400 mt-0.5">{formatDate(a.createdAt)}</p></div>
            </div>
          ))}
          {activity.length === 0 && <p className="px-5 py-6 text-center text-gray-400 text-sm">No recent activity</p>}
        </div>
      </div>

      {showModal && <LessonModal onClose={() => setShowModal(false)} onCreated={() => {}} />}
    </AppLayout>
  )
}
